/**
 * StageLog — the live receipt on sheet 13 (callouts 5, 6 and 7; mission 6 WP07
 * T040; FR-011; GEN.4, GEN.5, ANA.3, ANA.9, B.3; research §1.17; post-plan T1).
 *
 * Each stage of the run's path with its free or charged sublabel (register 7):
 * - Path A: Validation free, Generation one credit per record, Analysis free.
 *   The Analysis row says it applies all N rules, N from `['rules'].count`
 *   (13/6; C-004, never a literal and never the severity module's counts).
 * - Path B: Pre-check free, Analysis one credit per record. The rules sentence
 *   says analysis "is free", which is false on Path B, so it is not rendered
 *   there (copy request for WP13 in the WP07 handoff).
 * The credit position is `credits_reserved` with "reserved" (13/7; the chip is
 * the shell's), and the rule-set version is rendered as served (`not-run`
 * until ready; ANA.9).
 */
import { useQuery } from '@tanstack/react-query'

import { Skeleton } from '@/components/ui/skeleton'
import type { Run } from '@/lib/api/runs'
import { Mono, count } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { rulesQuery } from '@/lib/queries'

type Stage = { id: 'validation' | 'preCheck' | 'generation' | 'analysis'; charged: boolean }

const STAGES: Record<Run['path'], Stage[]> = {
  generate: [
    { id: 'validation', charged: false },
    { id: 'generation', charged: true },
    { id: 'analysis', charged: false },
  ],
  analyze: [
    { id: 'preCheck', charged: false },
    { id: 'analysis', charged: true },
  ],
}

/** 13/6: "applies all N rules", N as served; a skeleton while loading, nothing on a failed read. */
function AppliesAllRules() {
  const rules = useQuery(rulesQuery())
  if (rules.isPending) {
    return (
      <dd className="col-span-2">
        <Skeleton className="mt-1 h-4 w-3/4" />
      </dd>
    )
  }
  if (!rules.data) return null
  return (
    <dd className="col-span-2 mt-1 text-caption text-navy-600">
      {PROVISIONAL_COPY.appliesAllRules.render({ ruleCount: count(rules.data.count) })}
    </dd>
  )
}

export interface StageLogProps {
  run: Run
}

export function StageLog({ run }: StageLogProps) {
  const stages = STAGES[run.path] ?? STAGES.generate
  return (
    <dl data-stage-log="" className="flex flex-col gap-3 rounded-lg border border-navy-200 p-4 text-body">
      {/* mission 6 WP13: one div per stage holding its dt and dd directly (a grid, not a nested div), so axe's
          definition-list and dlitem rules pass on sheet 13 (NFR-003; WP11 handoff). */}
      {stages.map((stage) => (
        <div key={stage.id} data-stage={stage.id} className="grid grid-cols-[1fr_auto] items-baseline gap-x-3">
          <dt className="font-medium text-navy-950">{copyText(`stage.${stage.id}`)}</dt>
          <dd className="text-caption text-navy-600">
            {/* owner decision 2026-09-26 (decision 6): Path B charges per base segment. */}
            {copyText(stage.charged ? (run.path === 'analyze' ? 'sublabel.perBaseSegment' : 'sublabel.perRecord') : 'sublabel.free')}
          </dd>
          {stage.id === 'analysis' && !stage.charged ? <AppliesAllRules /> : null}
        </div>
      ))}
      {/* mission 6 WP13: handoff from WP07: RunHeader's labels, now from the register (fact.*). */}
      <div data-stage-credits="" className="flex items-baseline justify-between gap-3 border-t border-navy-200 pt-3">
        <dt className="text-navy-600">{copyText('fact.credits')}</dt>
        <dd className="text-navy-950">
          <Mono>{count(run.credits_reserved)}</Mono> {copyText('sublabel.reserved')}
        </dd>
      </div>
      <div data-stage-rule-set="" className="flex items-baseline justify-between gap-3">
        <dt className="text-navy-600">{copyText('fact.ruleSet')}</dt>
        <dd className="text-navy-950">
          <Mono>{run.rule_set_version}</Mono>
        </dd>
      </div>
    </dl>
  )
}
